import { Inject, Injectable, signal } from '@angular/core';
import { Resolve } from '@angular/router';

import {
  BehaviorSubject, Observable, of, Subject
} from 'rxjs';
import {
  distinctUntilChanged,
  map,
  switchMap, takeUntil,
  tap
} from 'rxjs/operators';

import { BillingClientProfile, UserDataService } from '@sondermind/data-access/client-facing/user-data';
import { AppErrorHandler } from '@sondermind/error-handling';
import { LocalizedDateService } from '@sondermind/utilities/time-and-date';
import { AUTH_SERVICE, IAuthService } from '@sondermind/utilities/models-auth';
import { IClientMatchingProfile } from '@sondermind/utilities/models-matching';

import { ClientUser, ClientUserProfile } from '@sondermind/utilities/models-user-client';

import { ClientUserTags } from '../enums/client-user-tags.enum';

import { MatchingProfileHttpService } from './matching-profile.httpservice';

@Injectable({
  providedIn: 'root'
})
export class CurrentUser implements Resolve<ClientUser> {
  constructor(
    private userData: UserDataService,
    @Inject(AUTH_SERVICE)
    private authSvc: IAuthService,
    private matchingProfileSvc: MatchingProfileHttpService,
    private errorHandler: AppErrorHandler,
    private localizedDateService: LocalizedDateService,
  ) {
    this.user$.subscribe((user) => {
      this.errorHandler.handleUserChange(user?.id?.toString());
    });
  }

  private userSub = new BehaviorSubject<ClientUser>(null as unknown as ClientUser);
  private pslClient = new BehaviorSubject<boolean>(false);
  private matchingProfileSubj = new BehaviorSubject<IClientMatchingProfile>(null as unknown as IClientMatchingProfile);
  private multipayorSub = new BehaviorSubject<boolean>(false);
  private initialized = false;
  private destroyed$ = new Subject<void>();
  private accountCompleteSubj = new BehaviorSubject<boolean>(false);
  private isFirstLogin = new BehaviorSubject<boolean>(false);

  user$ = this.userSub.asObservable();
  isPslClient$ = this.pslClient.asObservable();
  isMultipayor$ = this.multipayorSub.asObservable();
  matchingProfile$: Observable<IClientMatchingProfile> = this.matchingProfileSubj.asObservable();
  loggedIn$ = this.user$.pipe(map((u) => !!u));
  accountComplete$ = this.accountCompleteSubj.asObservable().pipe(distinctUntilChanged());
  readonly isVeteransAffairs = signal<boolean | undefined>(undefined);
  isPsychiatryClient: boolean = false;
  isHideProviderPhoto: boolean = false;
  isFirstLogin$ = this.isFirstLogin.asObservable();
  isAdmin: boolean = false;

  resolve(): ClientUser | Observable<ClientUser> {
    if (this.initialized) {
      return this.userSub.getValue();
    }
    return this.user$;
  }

  get user(): ClientUser {
    return this.userSub.getValue();
  }

  /**
   * The client's current device (browser) timezone. Used to render timestamps in the zone the
   * client is physically in, without mutating the persisted profile `timeZone` (which reflects
   * their address on file and is only changed from the account page).
   */
  get deviceTimeZone(): string {
    return this.localizedDateService.getBrowserTimezone();
  }

  get isPslClient(): boolean {
    return this.pslClient.getValue();
  }

  get isMultipayor(): boolean {
    return this.multipayorSub.getValue();
  }

  get loggedIn(): boolean {
    return this.authSvc.isAuthenticated() && !!this.userSub.getValue();
  }

  get personaUri(): string {
    return `sondermind://persona_client/${this.user.personaId}`;
  }

  get allowScheduling(): boolean {
    return this.loggedIn && this.user.allowScheduling;
  }

  get accountComplete(): boolean {
    return this.user != null && !this.user.needsSetup;
  }

  /**
   * Whether the client still needs to go through initial account onboarding.
   * Distinct from `accountComplete`: a previously-onboarded client can be
   * incomplete (an outstanding attestation, etc.) yet must not be routed back
   * through the new-client setup flow. See MER-4447.
   *
   * Falls back to `needsSetup` when the backend has not yet been deployed with
   * the `needsOnboarding` field, so an older API (or a deploy-order skew)
   * degrades to the prior behavior rather than silently letting new clients
   * skip setup.
   */
  get needsOnboarding(): boolean {
    if (this.user == null) {
      return true;
    }
    return this.user.needsOnboarding ?? this.user.needsSetup;
  }

  get matchingProfileData(): IClientMatchingProfile {
    return this.matchingProfileSubj.getValue();
  }

  get mostRecentReferralUnserviceable(): boolean {
    return this.user?.mostRecentReferralUnserviceable;
  }

  initialize$(): Observable<void> {
    // when oauth completes a round, refetch the user
    this.authSvc.authFlowComplete$.pipe(
      this.authSvc.initialized$.waitO(),
      switchMap((auth) => (auth ? this.fetch$() : of(null))),
      takeUntil(this.destroyed$),
    ).subscribe((user) => this.refresh(user as unknown as ClientUser));

    // when oauth is destroyed, reset the user
    this.authSvc.authDestroyed$.pipe(
      this.authSvc.initialized$.waitO(),
      takeUntil(this.destroyed$),
    ).subscribe(() => this.refresh(null as unknown as ClientUser));

    // make user fetching a part of the initialization pipe
    return this.authSvc.initialize().pipe(
      switchMap((auth) => (auth ? this.fetch$() : of(null))),
      tap((user) => {
        if (user) {
          // TODO: this doesn't seem to get called on first login after password set
          this.loadMatchingProfile();
          this.checkABTags(user);
          this.checkIdentifierTags(user);

          // Not needed on first login - looking for claims data, which can only be present after
          // a client has a scheduled therapist
          this.getClientConversionData();
        }

        this.refresh(user as unknown as ClientUser);
        this.initialized = true;
      }),
      switchMap((u) => (u ? this.reloadMultipayor$(u) : of(null))),
      tap((allowMultipayor) => this.refreshMultipayor(!!allowMultipayor)),
      map(() => undefined)
    );
  }

  // get matching profile data to add to user object
  loadMatchingProfile(): void {
    this.matchingProfileSvc.fetchMostRecent().subscribe((resp) => {
      this.matchingProfileSubj.next(resp);
    });
  }

  // tear down the CurrentUser service
  destroy(): void {
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  // noreturn
  login(returnTo: string): void {
    this.authSvc.login(returnTo ?? null);
  }

  logout$(): Observable<void> {
    return this.authSvc.logout();
  }

  setHasVisitedAccountSetup(personaClientId: number): void {
    this.userData.sendVisitedAccountSetupUrl(personaClientId);
  }

  getClientConversionData(): void {
    this.userData.getClientConversionData();
  }

  refresh(user: ClientUser): void {
    this.accountCompleteSubj.next(user != null && !user.needsSetup);
    // Push the authenticated profile timezone into the shared LocalizedDateService so the
    // LocalizedDatePipe (which passes no profileTimezone) renders in the client's profile zone;
    // clear it (undefined) on logout/null user.
    this.localizedDateService.setProfileTimezone(user?.profile?.timeZone);
    this.userSub.next(user);
  }

  refreshProfile(profile: ClientUserProfile): void {
    this.userSub.next({ ...this.userSub.value, profile });
  }

  refreshMultipayor(allowsMultipayor: boolean): void {
    this.multipayorSub.next(allowsMultipayor);
  }

  reload$(): Observable<void> {
    const isAccountComplete = this.accountComplete;
    // on forbidden, null out the user (not logged in)
    return this.fetch$().pipe(
      tap((u) => {
        this.checkABTags(u);
        this.checkIdentifierTags(u);
        this.loadMatchingProfile();
        this.refresh(u);
      }),
      switchMap((u) => this.reloadMultipayor$(u)),
      tap((allowMultipayor) => this.refreshMultipayor(!!allowMultipayor)),
      map(() => undefined)
    );
  }

  reloadMultipayor$(user: ClientUser): Observable<boolean | unknown> {
    return this.userData.billingProfileAllowsMultipayor(user.personaId);
  }

  setIsPslClient(isPslClient: boolean) {
    this.pslClient.next(isPslClient);
  }
  updateBillingClientProfile$(data: Partial<BillingClientProfile>): Observable<void> {
    if (this.user) {
      return this.userData.updateBillingClientProfileRequest(this.user.personaId, data).pipe(
        tap((allowsMultipayor) => this.multipayorSub.next(!!allowsMultipayor)),
        map(() => undefined)
      );
    }
    return this.reload$().pipe(
      switchMap(() => this.userData.updateBillingClientProfileRequest(this.user.personaId, data)),
      tap((allowsMultipayor) => this.multipayorSub.next(!!allowsMultipayor)),
      map(() => undefined)
    );
  }

  setFirstLogin(): void {
    this.isFirstLogin.next(true);
  }

  private fetch$(): Observable<ClientUser> {
    return this.userData.fetch({ null401: true }) as Observable<ClientUser>;
  }

  /**
   * Helper function for setting flags for A/B tests.
   *
   * EXPERIMENT HIDE PROVIDER PHOTO - PE-20447
   *
   */
  private checkABTags(user: ClientUser): void {
    this.isHideProviderPhoto = user.tags?.includes('EXPERIMENT HIDE PROVIDER PHOTO');
  }

  /**
   * Helper function to set flags based on tags not associated with AB tests.
   */
  private checkIdentifierTags(user: ClientUser): void {
    this.isVeteransAffairs.set(user.tags?.includes(ClientUserTags.VA_CLIENT));
    this.isPsychiatryClient = user.tags?.includes(ClientUserTags.PSYCHIATRY_CLIENT);
  }
}
